import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {browserHistory} from 'react-router';
import {List, Set} from 'immutable';
import throttle from 'lodash/throttle';

import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Button from '@material-ui/core/Button';
import Item from '@material-ui/core/MenuItem';

import {ModifyKpiRequest} from '@store/kpi/KpiTargetSource';
import {
  continuityString,
  exampleKpiValue,
  formatKpiValue,
  KpiTarget,
  KpiTargetContinuity,
  KpiTargetFrequency,
  KpiTargetInterface,
  KpiTargetNumericType,
  KpiTargetTree,
} from '@store/@types/kpi';
import KpiGoalActions from '@store/kpi/KpiTargetActions';
import {SkillId} from 'records/Skill';
import Breadcrumbs, {BreadcrumbLink} from 'components/pure/utils/Breadcrumbs';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import InputField from 'components/pure/utils/form/InputField';
import SingleSelect from '@components/single-select';

import SkillIdsSelectField from '$pages/@common/skill-ids-field';

import {KpiBulbIcon} from '@components/icon/KpiBulbIcon';
import {BullseyeIcon} from '@components/icon/BullseyeIcon';

import './KpiGoalForm.styl';
import formatSecondsShort from 'utils/formatSecondsShort';
import ClickableHelpIcon from 'components/pure/utils/ClickableHelpIcon';
import {getQuery} from 'utils/getQueryParam';
import {TrashIcon} from '@components/icon/TrashIcon';

const HelpText = {
  title:
    'The name of your new KPI. This will show up whenever this KPI is assigned to users, and should be a short description of the intended goal.',
  merge: `List of KPI Targets that will be merged to create a new KPI Goal`,
  metricType: `The unit of work that will be measured. This will also affect the formatting of the value when shown.`,
  target: `The concrete goal to be achieved.`,
  period: `The period of time in which the KPI should be measured. The target should be achievable within this time frame.`,
  continuity: `Whether this KPI should be a recurring goal, or if it should expire, and under what conditions.`,
  reporting: `Determines how the KPI entries will be reported on charts.`,
  skills: `The skills associated with this KPI. When a KPI's skill matches a Coaching Plan's skills that KPI will be visible during reviews.`,
};

const durationRegex = /^\d*\d\d:\d\d$/;

const freqSelections = Object.values<string>(KpiTargetFrequency).map(f => (
  <Item key={f} value={f}>
    {f.toLowerCase()}
  </Item>
));

interface EditableKpiTarget extends Omit<KpiTargetInterface, 'id' | 'createdByUserId' | 'value'> {
  durationValue: string;
  value: number | undefined;
}

const defaultState: EditableKpiTarget = Object.freeze({
  name: '',
  continuity: KpiTargetContinuity.ONE_TIME,
  frequency: KpiTargetFrequency.QUARTERLY,
  numericType: KpiTargetNumericType.CURRENCY,
  skillIds: Set() as Set<SkillId>,
  value: undefined as number | undefined,
  durationValue: '',
  chartAsSkill: true,
  chartAsEngagement: true,
});

interface KpiGoalFormProps {
  fetchGoalKpiTree: typeof KpiGoalActions.fetchGoalKpiTree;
  getKpiById: (id: any) => KpiTargetTree;
  params?: any;
  selectedKpiTargets: List<KpiTarget>;
}

const links: (isEdit: boolean, isGoal: boolean) => BreadcrumbLink[] = (isEdit, isGoal) => {
  if (isGoal) {
    return [
      {
        name: 'Performance Goals',
        url: '/admin/kpis',
        query: getQuery(location),
      },
      {
        name: 'Edit Performance Goal',
      },
    ];
  }

  return isEdit
    ? [
        {
          name: 'Performance Goals & KPIs',
          url: '/admin/kpis',
          query: getQuery(location),
        },
        {
          name: 'Edit KPI',
        },
      ]
    : [
        {
          name: 'Performance Goals & KPIs',
          url: '/admin/kpis',
          query: getQuery(location),
        },
        {
          name: 'Add KPI',
        },
      ];
};

/**
 * Handles creates and edits for KPIs and Goals
 */
export const KpiGoalForm: React.FC<KpiGoalFormProps> = ({
  fetchGoalKpiTree,
  getKpiById,
  params,
  selectedKpiTargets,
}) => {
  useEffect(() => {
    fetchGoalKpiTree().then().catch(console.error);
  }, [fetchGoalKpiTree]);

  const {kpiTarget, kpiChildren} = useMemo<KpiTargetTree>(() => {
    return getKpiById(params?.kpiId);
  }, [getKpiById, params]);

  const isEdit = !!kpiTarget;
  const [formState, setFormState] = useState<any>(defaultState);
  const preparedKpiChildren: List<KpiTarget> = isEdit ? kpiChildren : selectedKpiTargets || List();
  const isGoal = preparedKpiChildren && !preparedKpiChildren.isEmpty();

  const setField = useCallback(
    (field: keyof EditableKpiTarget, value: any) => {
      setFormState(s => ({
        ...s,
        [field]: value,
      }));
    },
    [setFormState]
  );
  const setSkillIds = useCallback(
    newValue => {
      setField('skillIds', newValue);
    },
    [setField]
  );

  useEffect(() => {
    if (kpiTarget?.id) {
      setFormState({
        name: kpiTarget.name,
        continuity: kpiTarget.continuity,
        frequency: kpiTarget.frequency,
        numericType: kpiTarget.numericType,
        skillIds: kpiTarget.skillIds,
        value: kpiTarget.value,
        durationValue:
          kpiTarget.numericType === KpiTargetNumericType.TIME
            ? formatSecondsShort(kpiTarget.value * 60, true, false, false)
            : undefined,
        chartAsSkill: kpiTarget.chartAsSkill,
        chartAsEngagement: kpiTarget.chartAsEngagement,
      });
    }
  }, [kpiTarget]);

  const skillsLabel = (
    <div className="help-label">
      Skills
      <ClickableHelpIcon text={HelpText.skills} />
    </div>
  );

  return (
    <div className="create-kpi">
      <h1>{isEdit ? 'Edit Goal' : 'Add KPI'}</h1>
      <Breadcrumbs links={links(isEdit, isGoal)} />
      <div className="create-form">
        <Title isGoal={isGoal} formState={formState} setFormState={setFormState} />
        <KpiMerge childKpis={preparedKpiChildren} />
        <MetricType formState={formState} setFormState={setFormState} />
        {formState.numericType === KpiTargetNumericType.TIME ? (
          <KpiValueTime isGoal={isGoal} formState={formState} setFormState={setFormState} />
        ) : (
          <KpiValue isGoal={isGoal} formState={formState} setFormState={setFormState} />
        )}
        <Frequency isGoal={isGoal} formState={formState} setFormState={setFormState} />
        <Continuity formState={formState} setFormState={setFormState} />
        <Reporting formState={formState} setFormState={setFormState} />
        <FieldContainer labelType="div" label={skillsLabel} className="skills-container">
          <SkillIdsSelectField
            style={{minWidth: 340}}
            onChange={setSkillIds}
            value={formState.skillIds}
          />
        </FieldContainer>
      </div>
      <div className="actions">
        <Button
          variant="contained"
          color="primary"
          disabled={!validate(formState)}
          onClick={() => submit(formState, preparedKpiChildren, kpiTarget)}>
          <span className="button-text">{isEdit ? 'Save Changes' : 'Add KPI'}</span>
        </Button>
        <Button onClick={() => browserHistory.push('/admin/kpis')}>
          <span className="button-text">Cancel</span>
        </Button>
      </div>
    </div>
  );
};

interface KpiGoalField {
  isGoal?: boolean;
  formState: EditableKpiTarget;
  setFormState(state: EditableKpiTarget | ((s: EditableKpiTarget) => EditableKpiTarget)): void;
}

function Frequency({isGoal, formState, setFormState}: KpiGoalField) {
  const label = (
    <div className="help-label">
      {isGoal ? 'Goal Period' : 'KPI Period'}
      <ClickableHelpIcon text={HelpText.period} />
    </div>
  );
  return (
    <FieldContainer labelType="div" label={label} className="frequency-container">
      <SingleSelect
        style={{width: 125}}
        getValue={option => option}
        getLabel={option => option.value}
        value={formState.frequency}
        onChange={e => {
          setFormState({
            ...formState,
            frequency: e.target.value as KpiTargetFrequency,
          });
        }}>
        {freqSelections}
      </SingleSelect>
    </FieldContainer>
  );
}

function Title({isGoal, formState, setFormState}: KpiGoalField) {
  const label = isGoal ? (
    <div className="help-label">
      <BullseyeIcon />
      <span className="goal-title-label">Goal Title</span>
      <ClickableHelpIcon text={HelpText.title} />
    </div>
  ) : (
    <div className="help-label">
      KPI Title
      <ClickableHelpIcon text={HelpText.title} />
    </div>
  );

  return (
    <FieldContainer
      labelType="div"
      errorOnBlur
      error={!formState.name && 'Field is required'}
      label={label}>
      <InputField
        className="title"
        placeholder="E.g. Hit 150k in sales in Q2"
        value={formState.name}
        onChange={name => setFormState({...formState, name: name as string})}
      />
    </FieldContainer>
  );
}

function Continuity({formState, setFormState}: KpiGoalField) {
  const ContinuityRadio = ({type}: {type: KpiTargetContinuity}) => (
    <div>
      <Radio
        className="radio"
        color="primary"
        name={type}
        checked={formState.continuity === type}
        onChange={e =>
          e.target.checked &&
          setFormState({
            ...formState,
            continuity: type,
          })
        }
      />
      <label>{continuityString(type)}</label>
    </div>
  );

  const label = (
    <div className="help-label">
      Continuity
      <ClickableHelpIcon text={HelpText.continuity} />
    </div>
  );

  return (
    <FieldContainer labelType="div" label={label}>
      <RadioGroup>
        <ContinuityRadio type={KpiTargetContinuity.ONE_TIME} />
        <ContinuityRadio type={KpiTargetContinuity.EXPIRING} />
        <ContinuityRadio type={KpiTargetContinuity.ONGOING} />
      </RadioGroup>
    </FieldContainer>
  );
}

function Reporting({formState, setFormState}: KpiGoalField) {
  const skillChecked = formState.chartAsSkill && !formState.chartAsEngagement;
  const engagementChecked = !formState.chartAsSkill && formState.chartAsEngagement;
  const eitherChecked = formState.chartAsSkill && formState.chartAsEngagement;
  const label = (
    <div className="help-label">
      Reporting
      <ClickableHelpIcon text={HelpText.reporting} />
    </div>
  );
  return (
    <FieldContainer labelType="div" label={label}>
      <RadioGroup>
        <div>
          <Radio
            className="radio"
            color="primary"
            checked={skillChecked}
            onChange={e =>
              e.target.checked &&
              setFormState({
                ...formState,
                chartAsSkill: true,
                chartAsEngagement: false,
              })
            }
          />
          <label>Report as Skill</label>
        </div>
        <div>
          <Radio
            className="radio"
            color="primary"
            checked={engagementChecked}
            onChange={e =>
              e.target.checked &&
              setFormState({
                ...formState,
                chartAsSkill: false,
                chartAsEngagement: true,
              })
            }
          />
          <label>Report as Activity / Engagement</label>
        </div>
        <div>
          <Radio
            className="radio"
            color="primary"
            checked={eitherChecked}
            onChange={e =>
              e.target.checked &&
              setFormState({
                ...formState,
                chartAsSkill: true,
                chartAsEngagement: true,
              })
            }
          />
          <label>Report as Either</label>
        </div>
      </RadioGroup>
    </FieldContainer>
  );
}

function MetricType({formState, setFormState}: KpiGoalField) {
  const MetricRadio = ({type}: {type: KpiTargetNumericType}) => {
    const label = {
      [KpiTargetNumericType.CURRENCY]: 'Currency',
      [KpiTargetNumericType.PERCENTAGE]: 'Percentage',
      [KpiTargetNumericType.AMOUNT]: 'Number / amount',
      [KpiTargetNumericType.TIME]: 'Time',
    }[type];

    return (
      <div>
        <Radio
          className="radio"
          color={'primary'}
          name={type}
          checked={formState.numericType === type}
          onChange={e =>
            e.target.checked &&
            setFormState({
              ...formState,
              numericType: type,
            })
          }
        />
        <label>{label}</label>
      </div>
    );
  };

  const fieldLabel = (
    <div className="help-label">
      Type of Metric
      <ClickableHelpIcon text={HelpText.metricType} />
    </div>
  );
  return (
    <FieldContainer labelType="div" label={fieldLabel}>
      <RadioGroup>
        <MetricRadio type={KpiTargetNumericType.CURRENCY} />
        <MetricRadio type={KpiTargetNumericType.PERCENTAGE} />
        <MetricRadio type={KpiTargetNumericType.AMOUNT} />
        <MetricRadio type={KpiTargetNumericType.TIME} />
      </RadioGroup>
    </FieldContainer>
  );
}

function KpiValue({formState, setFormState, isGoal}: KpiGoalField) {
  const {numericType: type, value} = formState;

  const handleChange = useCallback(
    v => {
      let prepared = v;
      if (
        type === KpiTargetNumericType.PERCENTAGE &&
        value &&
        v.replace(/[,]/g, '').length <= String(value).length
      ) {
        // this is required to fix backspace click
        // when % symbol was added at the end of line
        prepared = prepared.slice(0, prepared.length - 1);
      }
      const resValue = parseInt(prepared.replace(/[%$,]/g, ''), 10);
      setFormState(s => ({...s, value: resValue}));
    },
    [type, setFormState, value]
  );

  const error = !value ? 'Field is required' : value < 0 ? 'Value cannot be negative' : null;

  const label = (
    <div className="help-label">
      {isGoal ? 'Goal Target' : 'KPI Target'}
      <ClickableHelpIcon text={HelpText.target} />
    </div>
  );

  return (
    <FieldContainer labelType="div" errorOnBlur error={error} label={label}>
      <InputField
        className={`kpi-value ${type.toLowerCase()}`}
        type="text"
        placeholder={exampleKpiValue(type)}
        onChange={handleChange}
        value={value ? formatKpiValue(type, value) : ''}
      />
    </FieldContainer>
  );
}

function KpiValueTime({formState, setFormState, isGoal}: KpiGoalField) {
  const {durationValue} = formState;
  const valuePlaceholder = 'HH:MM';
  const handleChange = useCallback(
    v => setFormState(s => ({...s, durationValue: v})),
    [setFormState]
  );

  const error = !durationValue
    ? 'Field is required'
    : durationRegex.test(durationValue)
    ? null
    : 'Value must be formatted as HH:MM';

  const label = (
    <div className="help-label">
      {isGoal ? 'Goal Target' : 'KPI Target'}
      <ClickableHelpIcon text={HelpText.target} />
    </div>
  );

  return (
    <FieldContainer labelType="div" errorOnBlur error={error} label={label}>
      <InputField
        className={`kpi-value time`}
        type="text"
        placeholder={valuePlaceholder}
        onChange={handleChange}
        value={durationValue}
      />
    </FieldContainer>
  );
}

function KpiMerge({childKpis}: {childKpis: List<KpiTarget>}) {
  if (childKpis.isEmpty()) {
    return null;
  }

  const label = (
    <div className="help-label">
      Formed by Merging Kpis
      <ClickableHelpIcon text={HelpText.merge} />
    </div>
  );

  return (
    <FieldContainer labelType="div" label={label}>
      <div className="kpi-merge-options">
        {childKpis.map(kpi => (
          <div key={kpi.id} className="kpi-merge-row">
            <KpiBulbIcon />
            <span className="kpi-name">{kpi.name}</span>
            <TrashIcon onClick={() => KpiGoalActions.toggleKpi(kpi.id)} tooltip="Remove" />
          </div>
        ))}
      </div>
    </FieldContainer>
  );
}

function validate(formState: EditableKpiTarget): formState is Required<EditableKpiTarget> {
  if (!formState.name) return false;
  if (formState.numericType === KpiTargetNumericType.TIME) {
    if (!durationRegex.test(formState.durationValue ?? '')) return false;
  } else {
    if (!formState.value) return false;
    if (formState.value < 0) return false;
  }

  return true;
}

/* Return the value in minutes of the HH:MM string */
function parseDuration(duration: string): number {
  const [hours, minutes] = duration.split(':').map(s => parseInt(s, 10));
  return minutes + hours * 60;
}

const submit = throttle(
  async (formState: EditableKpiTarget, childKpis: List<KpiTarget>, kpi?: KpiTarget) => {
    if (!validate(formState)) {
      console.error('do something');
    } else {
      const params: ModifyKpiRequest = {
        continuity: formState.continuity,
        frequency: formState.frequency,
        name: formState.name,
        numericType: formState.numericType,
        skillIds: formState.skillIds?.toJS(),
        childIds: childKpis.map(k => k.id).toJS(),
        value:
          formState.numericType === KpiTargetNumericType.TIME
            ? parseDuration(formState.durationValue)
            : formState.value!,
        chartAsSkill: formState.chartAsSkill,
        chartAsEngagement: formState.chartAsEngagement,
      };
      try {
        if (kpi) {
          await KpiGoalActions.update(kpi.id, params);
        } else {
          await KpiGoalActions.create(params);
        }
        browserHistory.push('/admin/kpis');
      } catch (e) {
        // TODO Warn user
        console.error(e);
      }
    }
  },
  2000
);
